import { decodeFunctionData, type Abi, type Hex } from 'viem'
// import EthereumIcon from '../assets/icons/eth.svg'
import { getEthProvider } from '../utils/getEthProvider'
import { TransactionType, type GasData, type TokenData, type ContractInfo } from '../interfaces/eth'
import { logger } from '../logger'
import { fetchEnv } from './fetchEnv'
import { Chain } from '../interfaces/common'
import { getChainId } from './getChainId'

export const ETH_SUPPORTED_CHAINS = [Chain.ETH, Chain.ARB, Chain.BASE]
const TEST_ETH_ADDRESS = '0x343f1c4C2ac5750C9F9c22B2aC1aE93F4B9a6def'
const ethAddressRegex = /^0x[0-9a-fA-F]{40}$/

export const ETH_TRANSACTION_MAX_FEE = 23100

export const getTransactionType = (data: string | undefined) => {
  if (!data || data.length < 3) return TransactionType.ETH

  const method = data.slice(0, 10)
  switch (method) {
    case '0xa9059cbb':
      return TransactionType.TRANSFER
    case '0x095ea7b3':
      return TransactionType.APPROVE
    case '0xd0e30db0':
      return TransactionType.DEPOSIT
    default:
      return TransactionType.CONTRACT_CALL
  }
}

export const getGasLimit = (gasData: GasData, type: TransactionType) => {
  switch (type) {
    case TransactionType.TRANSFER:
      return BigInt(gasData.erc20TransferGas)
    case TransactionType.APPROVE || TransactionType.CONTRACT_CALL:
      return BigInt(gasData.erc20ApproveGas)
    case TransactionType.DEPOSIT:
      return BigInt(gasData.erc20DepositGas)
    default:
      return BigInt(ETH_TRANSACTION_MAX_FEE)
  }
}

const constructLogoURL = (name: string, symbol: string) => {
  return `https://cryptologos.cc/logos/${name
    .toLowerCase()
    .replace(' ', '-')}-${symbol.toLowerCase()}-logo.svg`
}

export const fetchTokenData = async (
  data: string | undefined,
  to: string | undefined,
  value: string | undefined,
  type: TransactionType
): Promise<TokenData> => {
  let receiver = to
  let amount = Number(value) || 0

  if (type === TransactionType.ETH || type === TransactionType.CONTRACT_CALL) {
    return {
      decimals: 18,
      // @ts-ignore
      logo: '',
      name: 'Ethereum',
      symbol: 'ETH',
      type,
      to: receiver || '',
      value: convertWeiToReadableEth(amount) || 0,
    }
  }

  const provider = getEthProvider(Chain.ETH)

  // Use viem's call method for ERC20 contract calls
  const erc20NameResult = await provider.call({
    to: to as `0x${string}`,
    data: '0x06fdde03', // name()
  })
  const erc20Name = decodeERC20String(erc20NameResult.data!)

  const erc20SymbolResult = await provider.call({
    to: to as `0x${string}`,
    data: '0x95d89b41', // symbol()
  })
  const erc20Symbol = decodeERC20String(erc20SymbolResult.data!)

  const erc20DecimalsResult = await provider.call({
    to: to as `0x${string}`,
    data: '0x313ce567', // decimals()
  })

  const decimals = parseInt(erc20DecimalsResult.data!.slice(-2), 16)

  if ((type === TransactionType.TRANSFER || type === TransactionType.APPROVE) && data) {
    // in case of approve and transfer, the recipient and amount are in the data field
    const { recipient, rawAmount, actualAmount } = parseERC20Data(data, decimals)
    receiver = recipient
    amount = actualAmount
  } else if (type === TransactionType.DEPOSIT) {
    amount = convertWeiToReadableEth(amount, decimals)
  }

  return {
    decimals: Number(decimals),
    logo: constructLogoURL(erc20Name, erc20Symbol),
    name: erc20Name,
    symbol: erc20Symbol,
    type,
    to: receiver || '',
    value: amount || 0,
  }
}

export const chooseSymbol = (symbol: string, type: TransactionType) => {
  switch (type) {
    case TransactionType.APPROVE:
    case TransactionType.TRANSFER:
      return symbol
    default:
      return 'ETH'
  }
}

async function request<T>(url: string): Promise<T> {
  const response = await fetch(url, {
    cache: 'no-cache',
  })
  return response.json() as Promise<T>
}

export const getGasData = (): Promise<GasData> => {
  return request('https://plug-cache.s3.amazonaws.com/gasData.json')
}

export const parseERC20Data = (data: string, decimals: number = 18) => {
  data = data.startsWith('0x') ? data.slice(2) : data

  const recipient = '0x' + data.slice(8, 72).replace(/^0+/, '')
  const valueHex = data.slice(72, 136)
  const rawAmount = BigInt('0x' + valueHex)

  const actualAmount = Number(rawAmount) / Math.pow(10, decimals)

  return {
    recipient,
    rawAmount,
    actualAmount,
  }
}

/*
  Fetches contract information from Etherscan API
  to simulate the transaction and get the gas limit
  and functions parameters
*/
export const callContractFunction = async (
  address: string,
  data: string | undefined,
  value: string | undefined,
  from: string | undefined,
  chain: Chain
): Promise<ContractInfo | null> => {
  try {
    if (!data) return null

    const response = await fetch(
      `https://api.etherscan.io/v2/api?chainid=${getChainId(chain)}&module=contract&action=getabi&address=${address}&apikey=${fetchEnv(
        'ETHERSCAN_SECRET'
      )}`
    )
    const abiResponse = await response.json()
    const parsedAbi = JSON.parse(abiResponse.result) as Abi

    const provider = getEthProvider(chain)

    let abiItem: any = null
    let decodedParams: any = null

    try {
      const decoded = decodeFunctionData({
        abi: parsedAbi,
        data: data as Hex,
      })

      abiItem = parsedAbi.find(
        (item: any) => item.type === 'function' && item.name === decoded.functionName
      )

      decodedParams = decoded.args
    } catch (error) {
      logger.debug('Error decoding function data', { originalError: error })
      return null
    }

    if (abiItem) {
      let estimatedGas = BigInt(0)

      try {
        estimatedGas = await provider.estimateGas({
          to: address as `0x${string}`,
          data: data as Hex,
          account: (from || TEST_ETH_ADDRESS) as `0x${string}`,
          value: value ? BigInt(value) : 0n,
        })
      } catch (error) {
        logger.debug('Error estimating gas for contract method', { originalError: error })
      }

      return {
        gasLimit: estimatedGas,
        name: abiItem.name,
        functionType: abiItem.stateMutability,
        inputs: abiItem.inputs,
        params: decodedParams,
        value: value,
      }
    }
  } catch (error) {
    logger.debug('Error fetching contract info', { originalError: error })
  }

  return null
}

export const convertWeiToReadableEth = (weiValue: number, decimals: number = 18): number => {
  return weiValue / Math.pow(10, decimals)
}

export const truncateEthAddress = (address: string): string => {
  if (!address) return ''
  const prefix = address.slice(0, 6)
  const suffix = address.slice(-4)
  return `${prefix}...${suffix}`
}

export const decodeERC20String = (hexString: string) => {
  hexString = hexString.replace('0x', '')

  const length = parseInt(hexString.slice(64, 128), 16)

  const stringHex = hexString.slice(128, 128 + length * 2)

  return Buffer.from(stringHex, 'hex').toString('utf8')
}

export const calculateTransactionFee = (
  gasLimit: BigInt | undefined,
  maxFeePerGas: BigInt,
  maxPriorityFeePerGas: BigInt
) => {
  const gasUnits = gasLimit || 21000
  const totalFeePerGas = Number(maxFeePerGas) + Number(maxPriorityFeePerGas)
  const feeInWei = Number(gasUnits) * totalFeePerGas
  return (feeInWei / 10 ** 18).toString()
}

export const validateEthTransaction = (params: any) => {
  try {
    if (!params) throw new Error('No params provided')
    if (params.gasPrice) throw new Error('Legacy gasPrice is not supported')
    if (params.to && !ethAddressRegex.test(params.to)) throw new Error('Invalid address')
    if (params.from && !ethAddressRegex.test(params.from)) throw new Error('Invalid address')
    if (params.data && !params.data.startsWith('0x')) throw new Error('Invalid data')
    if (params.gasLimit) BigInt(params.gasLimit)
    if (params.maxFeePerGas) BigInt(params.maxFeePerGas)
    if (params.maxPriorityFeePerGas) BigInt(params.maxPriorityFeePerGas)
    if (params.nonce) BigInt(params.nonce)
    if (params.value) BigInt(params.value)

    return true
  } catch {
    return false
  }
}
